import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Globe } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import Flag from './Flag';

/**
 * LanguageOnboardingBanner — one-shot floating card that surfaces the
 * 7 supported languages at the top of the page on the visitor's first
 * encounter with the app, so they know language switching exists.
 *
 * Behavior:
 *   • Renders once per browser, gated on `localStorage` key
 *     `lang_onboarding_seen`. Dismissed → never shown again.
 *   • The LanguageSwitcher also sets that key when used explicitly,
 *     so picking a language from the header pill ALSO dismisses
 *     this banner permanently (no double-prompt).
 *   • Clicking a flag = changes language AND dismisses.
 *   • Auto-dismisses after 12 seconds if ignored (so it doesn't
 *     linger forever above the page).
 *
 * Intended to be mounted ONLY on customer-facing pages (admin is
 * strictly English — handled by the caller).
 */
const SEEN_KEY = 'lang_onboarding_seen';
const AUTO_DISMISS_MS = 12000;

export default function LanguageOnboardingBanner() {
  const { i18n, t } = useTranslation('common');
  const { token } = useAuth() || {};
  const [visible, setVisible] = useState(false);

  // Decide whether to surface the banner on mount.
  useEffect(() => {
    let cancelled = false;
    const decide = () => {
      try {
        if (localStorage.getItem(SEEN_KEY) === '1') return;
      } catch (_) { /* private mode → still show, harmless */ }
      // Tiny delay so the page lays out before the banner pops in;
      // avoids a flash of misaligned content during initial mount.
      const tid = setTimeout(() => { if (!cancelled) setVisible(true); }, 600);
      return () => clearTimeout(tid);
    };
    return decide();
  }, []);

  // Auto-dismiss timer — only runs while the banner is on screen.
  useEffect(() => {
    if (!visible) return;
    const tid = setTimeout(() => {
      dismiss(false);
    }, AUTO_DISMISS_MS);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = (markSeen = true) => {
    if (markSeen) {
      try { localStorage.setItem(SEEN_KEY, '1'); } catch (_) { /* private mode */ }
    }
    setVisible(false);
  };

  const pick = async (code) => {
    if (code !== i18n.language) {
      i18n.changeLanguage(code);
      if (token) {
        try {
          const api = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
          await fetch(`${api}/api/me/language`, {
            method:  'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body:    JSON.stringify({ language: code }),
          });
        } catch (_) { /* ignore */ }
      }
    }
    dismiss(true);
  };

  if (!visible) return null;

  // Detect current code for the active highlight (handles `zh` alias).
  const currentCode = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)?.code
    || SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code))?.code
    || 'en';

  return (
    <div
      data-testid="language-onboarding-banner"
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[90] w-[min(560px,calc(100vw-24px))] animate-[slideDown_0.35s_ease-out]"
      role="dialog"
      aria-label={t('language_switcher.title', { defaultValue: 'Change language' })}
    >
      <div className="relative rounded-2xl border border-cyan-200/60 bg-white/95 backdrop-blur-md shadow-2xl ring-1 ring-cyan-500/10 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-600 grid place-items-center shadow-md">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-slate-800 leading-tight">
              {t('language_onboarding.title', { defaultValue: 'Choose your language' })}
            </div>
            <div className="text-[11px] text-slate-500 leading-tight mt-0.5">
              {t('language_onboarding.subtitle', { defaultValue: 'You can change this anytime from the header' })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => dismiss(true)}
            data-testid="language-onboarding-dismiss"
            className="flex-shrink-0 w-7 h-7 grid place-items-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            aria-label={t('common.close', { defaultValue: 'Close' })}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 md:grid-cols-11 gap-1.5">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === currentCode;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => pick(lang.code)}
                data-testid={`language-onboarding-${lang.code}`}
                title={lang.native}
                className={`flex flex-col items-center gap-1 rounded-lg py-2 px-1 transition-all border ${
                  active
                    ? 'border-cyan-400 bg-cyan-50 shadow-sm scale-105'
                    : 'border-slate-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/50 hover:scale-105'
                }`}
              >
                <Flag code={lang.code} className="w-6 h-[18px]" title={lang.label} />
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${active ? 'text-cyan-700' : 'text-slate-600'}`}>
                  {lang.code === 'zh-CN' ? 'ZH' : lang.code}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from { transform: translate(-50%, -20px); opacity: 0; }
          to   { transform: translate(-50%, 0);     opacity: 1; }
        }
      `}</style>
    </div>
  );
}
